import ReactDOM from "react-dom";
import { Button, ButtonVariations } from "../buttons/button";
import { Icon } from "../icons/icon";

import classNames from "classnames";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import { ReactNode } from "react";
import { useEnvironment } from "../../customHooks/useEnvironment";

export const ModalComponent = ({
  isModalOpen = false,
  onModalClose = () => {},
  title = "",
  topTitle = false,
  subTitle,
  children,
  childrenClassName = "",
  subtitleContainerClassName = "",
  modalClassName = "",
  hasButtons = true,
  proceedButtonText = "Continue",
  proceedButtonIcon = undefined,
  proceedButtonVariant = "primary",
  proceedButtonClassName = "",
  onProceedClick = () => {},
  hasCancelButton = false,
  cancelButtonText = "Cancel",
  cancelButtonVariant = "secondary-light-2",
  onCancelClick = () => {},
  isLoading = false,
  isProceedDisabled = false,
  buttonsContainerClassName = "",
  modalStyle = "center",
  isMenuModal = false,
  showHeader = true,
  showMobileTopBar = true,
  closeOnOutsideClick = true,
}: {
  isModalOpen?: boolean;
  onModalClose?: () => void;
  title?: ReactNode;
  topTitle?: boolean; //Is modal title on same line as close Icon or below it
  subTitle?: ReactNode;
  children?: ReactNode;
  childrenClassName?: string;
  subtitleContainerClassName?: string;
  modalClassName?: string;
  hasButtons?: boolean;
  proceedButtonText?: string;
  proceedButtonIcon?: string;
  proceedButtonVariant?: ButtonVariations;
  proceedButtonClassName?: string;
  onProceedClick?: () => void;
  hasCancelButton?: boolean;
  cancelButtonText?: string;
  cancelButtonVariant?: ButtonVariations;
  onCancelClick?: () => void;
  isLoading?: boolean;
  isProceedDisabled?: boolean;
  buttonsContainerClassName?: string;
  modalStyle?: "side" | "center";
  isMenuModal?: boolean;
  showHeader?: boolean;
  showMobileTopBar?: boolean;
  closeOnOutsideClick?: boolean;
}) => {
  const { environment } = useEnvironment();

  const controls = useDragControls();

  const y = useMotionValue(0);
  const x = useMotionValue(0);

  return isModalOpen
    ? ReactDOM.createPortal(
        <motion.div
          initial={{ backgroundColor: "rgb(0 0 0 / 0.0)" }}
          animate={{ backgroundColor: "rgb(0 0 0 / 0.7)" }}
          className={classNames(
            `
            !fixed inset-0 !z-[999] touch-none
            flex items-end md:items-center hide-scroll-bar
            `,
            {
              "md:!p-0 justify-end": modalStyle === "side",
              "p-0 md:p-[20px] justify-center":
                modalStyle === "center" && !isMenuModal,
              "p-4 pb-8 md:p-[20px] justify-center": isMenuModal && isMenuModal,
            }
          )}
          onClick={(e) => {
            e.stopPropagation();

            if (closeOnOutsideClick) {
              onModalClose();
            }
          }}
        >
          <motion.div
            drag={modalStyle === "center" ? "y" : "x"}
            initial={modalStyle === "center" ? { y: "50%" } : { x: "100%" }}
            animate={modalStyle === "center" ? { y: "0%" } : { x: "0%" }}
            exit={modalStyle === "center" ? { y: "50%" } : { x: "100%" }}
            transition={{ ease: "easeInOut" }}
            dragControls={controls}
            style={{ y, x }}
            dragListener={modalStyle === "center" ? false : false}
            dragConstraints={
              modalStyle === "center"
                ? {
                    top: 0,
                    bottom: 0,
                  }
                : {
                    left: 0,
                    right: 0,
                  }
            }
            dragElastic={
              modalStyle === "center"
                ? {
                    top: 0,
                    bottom: 0.5,
                  }
                : {
                    left: 0,
                    right: 0.5,
                  }
            }
            onDragEnd={() => {
              if (
                (y.get() >= 100 && modalStyle === "center") ||
                (x.get() >= 5 && modalStyle === "side")
              ) {
                onModalClose();
              }
            }}
            className={classNames(
              `
                bg-white overscroll-none
                w-full md:max-w-[407px] h-fit
                p-[24px] md:p-[40px]
                flex flex-col overflow-scroll relative justify-between hide-scroll-bar
                `,
              {
                "h-screen max-h-[calc(100dvh)] rounded-none !pt-[calc(44px)]":
                  modalStyle === "side" && environment === "mobile",
                "h-screen max-h-[calc(100dvh)] rounded-none":
                  modalStyle === "side" && environment !== "mobile",
                "max-h-[80%] rounded-t-[8px] md:rounded-[4px]":
                  modalStyle === "center" && !isMenuModal,
                "max-h-[80%] rounded-[16px] md:rounded-[4px]":
                  modalStyle === "center" && isMenuModal,
              },
              modalClassName,
              {
                "max-md:!pt-14": showMobileTopBar && modalStyle === "center",
              }
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {showMobileTopBar && modalStyle === "center" ? (
              <button
                type="button"
                className="pt-4 pb-10 absolute top-0 md:hidden flex left-0 right-0 items-center justify-center cursor-grab touch-none"
                onPointerDown={(e) => {
                  e.stopPropagation();
                  controls.start(e);
                }}
              >
                <hr className="h-1.5 w-10 rounded-xl bg-grey-400" />
              </button>
            ) : undefined}

            {showHeader ? (
              <div
                className={classNames(
                  `flex w-full items-center ${
                    topTitle && !!title ? "justify-between" : "justify-end"
                  }`,
                  {
                    "max-md:hidden": showMobileTopBar && !topTitle,
                  }
                )}
              >
                {!!title && topTitle ? (
                  <div className="text-base md:text-[20px] font-bold">
                    {title}
                  </div>
                ) : undefined}

                <button
                  type="button"
                  className={classNames("bg-white rounded-full w-fit p-1", {
                    "max-md:hidden": showMobileTopBar,
                  })}
                  onClick={() => {
                    onModalClose();
                  }}
                >
                  <Icon
                    svg="cancel-icon"
                    width={24}
                    height={24}
                    className="hover:!cursor-pointer hover:!scale-90"
                  />
                </button>
              </div>
            ) : undefined}

            {!!title && !topTitle ? (
              <div className="text-base mt-6 md:mt-8 md:text-[20px] font-bold">
                {title}
              </div>
            ) : undefined}

            {subTitle ? (
              <div
                className={`
                  mt-1 text-sm text-pry-dark-green-300/80
                  w-full max-w-[308px]
                  ${subtitleContainerClassName}
                  `}
              >
                {subTitle}
              </div>
            ) : undefined}

            <div
              className={`mt-6 flex-1 overflow-y-scroll hide-scroll-bar ${childrenClassName}`}
            >
              {children}
            </div>

            {hasButtons ? (
              <div
                className={`
                  flex flex-row flex-wrap gap-2 mt-6
                  items-center justify-between
                  ${buttonsContainerClassName}
                  `}
              >
                {hasCancelButton && (
                  <Button
                    text={cancelButtonText}
                    onClick={onCancelClick}
                    type="button"
                    className="flex-1"
                    loading={false}
                    disabled={false}
                    variation={cancelButtonVariant}
                  />
                )}

                <Button
                  text={proceedButtonText}
                  onClick={onProceedClick}
                  type="submit"
                  className={`flex-1 ${proceedButtonClassName}`}
                  loading={isLoading}
                  disabled={isProceedDisabled}
                  variation={proceedButtonVariant}
                  icon={proceedButtonIcon}
                />
              </div>
            ) : undefined}
          </motion.div>
        </motion.div>,
        document.getElementById("portal")!
      )
    : null;
};
